import { FC, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalFooter, Button, Select, Textarea } from '@/components/ui';
import { pitchSchema, type PitchFormData } from '@/validations';
import { Pitch, PitchStrategy } from '../types';

interface PitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pitch: Pitch) => void;
  pitch?: Pitch;
  mode: 'create' | 'edit';
  targetOptions: string[];
}

/**
 * Pitch Creation/Edit Modal
 * Uses react-hook-form with generic form components (like wizard steps)
 */
export const PitchModal: FC<PitchModalProps> = ({
  isOpen,
  onClose,
  onSave,
  pitch,
  mode,
  targetOptions,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PitchFormData>({
    resolver: zodResolver(pitchSchema),
    defaultValues: {
      targetLabel: pitch?.targetLabel || targetOptions[0] || '',
      strategy: pitch?.strategy || 'Consultative', // Default to first enum value
      text: pitch?.text || '',
    },
  });

  // Reset form when pitch changes
  useEffect(() => {
    if (isOpen) {
      reset({
        targetLabel: pitch?.targetLabel || targetOptions[0] || '',
        strategy: pitch?.strategy,
        text: pitch?.text || '',
      });
    }
  }, [pitch, isOpen, targetOptions, reset]);

  const onSubmit = (data: PitchFormData) => {
    const pitchData: Pitch = {
      ...pitch, // Preserve id if editing
      targetLabel: data.targetLabel,
      strategy: data.strategy as PitchStrategy,
      text: data.text,
    };
    onSave(pitchData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Create Pitch' : 'Edit Pitch'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Target */}
        <Controller
          name="targetLabel"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label="Target"
              options={targetOptions.map((opt) => ({ value: opt, label: opt }))}
              fullWidth
              error={errors.targetLabel?.message}
            />
          )}
        />

        {/* Strategy */}
        <Controller
          name="strategy"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value || ''}
              label="Strategy"
              options={[
                { value: '', label: 'Select a strategy' },
                ...Object.values(PitchStrategy).map((strategy) => ({
                  value: strategy,
                  label: strategy,
                })),
              ]}
              fullWidth
              error={errors.strategy?.message}
            />
          )}
        />

        {/* Text (Optional) */}
        <Controller
          name="text"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              label="Pitch Text"
              placeholder="Enter your sales pitch text..."
              rows={4}
              fullWidth
              optional
              error={errors.text?.message}
            />
          )}
        />

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save Pitch
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
